export interface AppSetupStep {
  title: string;
  detail: string;
  /** Shown in place of the Open button, for a step that happens outside Littlebird */
  trailingLabel?: string;
}

/** What a catalog server needs from its Mac app, shown when connecting and on its General tab */
export interface AppSetup {
  appName: string;
  /** Must be listed in `APP_LAUNCH_URLS`, or main refuses to open it */
  launchUrl: string;
  requiresFullDiskAccess: boolean;
  /** Puts a connection check with a Test again button at the top of the General tab */
  showsConnectionStatus?: boolean;
  connectSteps: readonly AppSetupStep[];
  installedSteps: readonly AppSetupStep[];
}

const OMNIFOCUS_STEP_DETAIL =
  'Littlebird works through the OmniFocus app on this Mac. It opens OmniFocus when it needs it.';

export const APP_SETUP_BY_TEMPLATE: Readonly<
  Partial<Record<string, AppSetup>>
> = {
  things: {
    appName: 'Things',
    launchUrl: 'things:///',
    requiresFullDiskAccess: true,
    connectSteps: [
      {
        title: 'Turn on Things URLs',
        detail:
          'In Things, open Settings, then General, and turn on “Enable Things URLs”. Without it, Littlebird can read your to-dos but can’t add or change them.',
      },
    ],
    installedSteps: [
      {
        title: 'Things URLs',
        detail:
          'To let Littlebird add and change to-dos, open Settings in Things, then General, and turn on “Enable Things URLs”.',
      },
    ],
  },
  fantastical: {
    appName: 'Fantastical',
    launchUrl: 'x-fantastical3://',
    requiresFullDiskAccess: true,
    connectSteps: [
      {
        title: 'Open Fantastical once',
        detail:
          'Littlebird reads your events from Fantastical’s own data, which exists only after Fantastical has opened on this Mac.',
      },
    ],
    installedSteps: [
      {
        title: 'New events',
        detail:
          'Littlebird sends new events to Fantastical, which shows each one for you to confirm before it’s added.',
      },
    ],
  },
  omnifocus: {
    appName: 'OmniFocus',
    launchUrl: 'omnifocus:///inbox',
    requiresFullDiskAccess: false,
    connectSteps: [
      { title: 'Keep OmniFocus installed', detail: OMNIFOCUS_STEP_DETAIL },
    ],
    installedSteps: [{ title: 'OmniFocus app', detail: OMNIFOCUS_STEP_DETAIL }],
  },
  imessage: {
    appName: 'Messages',
    launchUrl: 'messages://',
    requiresFullDiskAccess: true,
    showsConnectionStatus: true,
    connectSteps: [
      {
        title: 'Sign in to Messages',
        detail:
          'Open Messages and make sure you’re signed in with your Apple Account.',
      },
      {
        title: 'Littlebird can send messages',
        detail:
          'The first time Littlebird sends a message, macOS asks to let it control Messages. Choose OK.',
        trailingLabel: 'Asked on first use',
      },
    ],
    installedSteps: [
      {
        title: 'Messages account',
        detail:
          'Littlebird reads and sends through the account signed in to Messages on this Mac.',
      },
    ],
  },
};
